import {
  ChakraBox,
  ChakraDivider,
  ChakraFlex,
  ChakraGrid,
  DropdownOption,
  DropdownV2,
  InputGroupV2,
  TagDropdown,
  TagProps,
} from '@equip.health/ui';
import {
  useGetTreatmentDiscussionsOfWeightEnumQuery,
  useGetTreatmentEpisodeLevelsEnumQuery,
  useGetTreatmentModalitiesEnumQuery,
  useGetTreatmentSaferStagesEnumQuery,
} from '~/store/services/enumerations';
import { useMemo } from 'react';
import {
  TreatmentAttributeEnum,
  treatmentAttributeEnumToDropdownOption,
} from '~/lib/types/treatmentAttributes/treatmentAttributes';
import Loading from '~/pages/loading';
import { useFormikContext } from 'formik';
import { EditTreatmentOverviewFormData } from '~/lib/types/notes/EditTreatmentOverviewFormData';
import { TreatmentLevelIdToTagColorMap } from './TreatmentLevelIdToTagColorMap';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';

const ID = 'edit-treatment-overview-form-attributes-step';
export const EditTreatmentOverviewFormAttributesStep = () => {
  const { trackEvent } = useAnalytics();
  const {
    values: formValues,
    setFieldValue,
    setValues,
  } = useFormikContext<EditTreatmentOverviewFormData>();

  const { modality, form, phaseStage, level, discussionOfWeight, saferStage } =
    formValues;
  const {
    data: treatmentModalities,
    isLoading: isGetTreatmentModalitiesLoading,
  } = useGetTreatmentModalitiesEnumQuery();
  const {
    data: treatmentEpisodeLevels,
    isLoading: isGetTreatmentEpisodeLevelsLoading,
  } = useGetTreatmentEpisodeLevelsEnumQuery();
  const {
    data: discussionsOfWeight,
    isLoading: isGetTreatmentDiscussionsOfWeightLoading,
  } = useGetTreatmentDiscussionsOfWeightEnumQuery();
  const { data: saferStages, isLoading: isGetSaferStagesLoading } =
    useGetTreatmentSaferStagesEnumQuery();

  const isLoading =
    isGetTreatmentModalitiesLoading ??
    isGetTreatmentEpisodeLevelsLoading ??
    isGetTreatmentDiscussionsOfWeightLoading ??
    isGetSaferStagesLoading;

  const modalityOptions: DropdownOption[] = useMemo(
    () =>
      treatmentModalities?.map((tm) => ({
        label: tm.label,
        value: tm.code,
      })),
    [treatmentModalities],
  );

  const selectedModality = treatmentModalities?.find(
    (tm) => tm.code === modality?.code,
  );

  const formOptions: DropdownOption[] = useMemo(
    () =>
      selectedModality?.treatmentForms.map((form) => ({
        label: form.label,
        value: form.code,
      })) ?? [],
    [treatmentModalities, modality?.code],
  );

  let phaseStages: TreatmentAttributeEnum[] = [];
  if (selectedModality?.treatmentForms.length > 0) {
    phaseStages = selectedModality.treatmentForms.find(
      (modalityForm) => modalityForm.code === form?.code,
    )?.treatmentPhaseStages;
  } else if (selectedModality?.phaseStages.length > 0) {
    phaseStages = selectedModality.phaseStages;
  }
  const phaseStageOptions =
    phaseStages?.map(treatmentAttributeEnumToDropdownOption) ?? [];

  const levelOptions: TagProps[] = useMemo(
    () =>
      treatmentEpisodeLevels?.map((level) => ({
        color: TreatmentLevelIdToTagColorMap[level.id.toString(10)],
        id: level.id.toString(10),
        value: level.label,
      })) ?? [],
    [treatmentEpisodeLevels],
  );
  const levelValue = levelOptions?.find(
    (option) => option.id === level?.id.toString(),
  )?.value;

  const discussionsOfWeightOptions: DropdownOption[] = useMemo(
    () => discussionsOfWeight?.map(treatmentAttributeEnumToDropdownOption),
    [discussionsOfWeight],
  );
  const saferStageOptions: DropdownOption[] = useMemo(
    () => saferStages?.map(treatmentAttributeEnumToDropdownOption),
    [saferStages],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ChakraFlex flexDirection="column">
      <InputGroupV2 id={`${ID}-modality-group`} label="Modality">
        <DropdownV2
          ariaLabel="Modality"
          id={`${ID}-modality`}
          isResettable={false}
          menuOptions={modalityOptions}
          onDropdownOpen={() =>
            trackEvent(CarePlanEvents.clickOnModalityDropdownMenu)
          }
          onOptionClick={(newModalityCode) => {
            trackEvent(CarePlanEvents.clickOnModalityDropdownMenuOption);
            if (newModalityCode !== modality?.code) {
              setValues((prev) => ({
                ...prev,
                form: null,
                modality: treatmentModalities.find(
                  (tm) => tm.code === newModalityCode,
                ),
                phaseStage: null,
              }));
            }
          }}
          value={modality?.code}
        />
      </InputGroupV2>
      <ChakraFlex gap="12px" width="100%">
        {!!modality && formOptions.length > 0 && (
          <ChakraBox flex={1}>
            <InputGroupV2 id={`${ID}-form-group`} label="Form">
              <DropdownV2
                ariaLabel="Form"
                id={`${ID}-form`}
                isResettable={false}
                menuOptions={formOptions}
                onDropdownOpen={() =>
                  trackEvent(CarePlanEvents.clickOnFormDropdownMenu)
                }
                onOptionClick={(newFormCode) => {
                  trackEvent(CarePlanEvents.clickOnFormDropdownMenuOption);
                  if (newFormCode !== form?.code) {
                    setValues((prev) => ({
                      ...prev,
                      form: selectedModality.treatmentForms.find(
                        (f) => f.code === newFormCode,
                      ),
                      phaseStage: null,
                    }));
                  }
                }}
                value={form?.code}
              />
            </InputGroupV2>
          </ChakraBox>
        )}
        {!!modality && (!!form || formOptions.length === 0) && (
          <ChakraBox flex={1}>
            <InputGroupV2 id={`${ID}-phase-stage-group`} label="Phase/stage">
              <DropdownV2
                ariaLabel="Phase/stage"
                id={`${ID}-phase-stage`}
                isResettable={false}
                menuOptions={phaseStageOptions}
                onDropdownOpen={() =>
                  trackEvent(CarePlanEvents.clickOnPhaseStageDropdownMenu)
                }
                onOptionClick={(newPhaseStageCode) => {
                  trackEvent(
                    CarePlanEvents.clickOnPhaseStageDropdownMenuOption,
                  );
                  setFieldValue(
                    'phaseStage',
                    phaseStages?.find((ps) => ps.code === newPhaseStageCode),
                  );
                }}
                value={phaseStage?.code}
              />
            </InputGroupV2>
          </ChakraBox>
        )}
      </ChakraFlex>
      <ChakraDivider color="neutral.line" marginY="24px" />
      <ChakraGrid
        gridColumnGap="24px"
        gridRowGap="12px"
        templateColumns="1fr 1fr 1fr"
        templateRows="1fr"
      >
        <InputGroupV2 id={`${ID}-level-group`} label="Level">
          <TagDropdown
            id={`${ID}-level`}
            isResettable={false}
            menuOptions={levelOptions}
            onDropdownOpen={() =>
              trackEvent(CarePlanEvents.clickOnLevelDropdownMenu)
            }
            onOptionClick={(newLevelIdStr) => {
              trackEvent(CarePlanEvents.clickOnLevelDropdownMenuOption);
              if (!newLevelIdStr) {
                setFieldValue('level', undefined);
              }
              const newLevel = treatmentEpisodeLevels.find(
                (level) => level.id === parseInt(newLevelIdStr),
              );
              if (newLevel) {
                setFieldValue('level', {
                  id: newLevel.id,
                  label: newLevel.label,
                  sortOrder: newLevel.sortOrder,
                });
              }
            }}
            value={levelValue}
            variant="outline"
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${ID}-discussion-of-weight-group`}
          label="Weight discussion"
        >
          <DropdownV2
            ariaLabel="Weight discussion"
            id={`${ID}-discussion-of-weight`}
            isResettable={false}
            menuOptions={discussionsOfWeightOptions}
            onDropdownOpen={() =>
              trackEvent(CarePlanEvents.clickOnWeightDiscussionDropdownMenu)
            }
            onOptionClick={(newCode) => {
              trackEvent(
                CarePlanEvents.clickOnWeightDiscussionDropdownMenuOption,
              );
              setFieldValue(
                'discussionOfWeight',
                discussionsOfWeight.find((dow) => dow.code === newCode),
              );
            }}
            value={discussionOfWeight?.code}
          />
        </InputGroupV2>
        <InputGroupV2 id={`${ID}-safer-stage-group`} label="SAFER stage">
          <DropdownV2
            ariaLabel="SAFER stage"
            id={`${ID}-safer-stage`}
            isResettable={false}
            menuOptions={saferStageOptions}
            onDropdownOpen={() =>
              trackEvent(CarePlanEvents.clickOnSAFERStageDropdownMenu)
            }
            onOptionClick={(newCode) => {
              trackEvent(CarePlanEvents.clickOnSAFERStageDropdownMenuOption);
              setFieldValue(
                'saferStage',
                saferStages.find((s) => s.code === newCode),
              );
            }}
            value={saferStage?.code}
          />
        </InputGroupV2>
      </ChakraGrid>
    </ChakraFlex>
  );
};
